import {
  Activity,
  ArrowUpRight,
  CircleDollarSign,
  Gauge,
  ShieldCheck,
} from "lucide-react";
import { AlertCard } from "../components/AlertCard";
import { ClientDetail } from "../components/ClientDetail";
import { LoadingState } from "../components/LoadingState";
import { MetricCard } from "../components/MetricCard";
import { useDashboard } from "../context/DashboardContext";
import { currency, pacingLabel } from "../lib/format";

export function ClientPage() {
  const {
    data,
    session,
    selectedClient,
    setSelectedClient,
    selectedClientHealthState,
    loading,
  } = useDashboard();
  if (loading && !data) return <LoadingState />;

  const isClientScoped = Boolean(session?.isClientUser || session?.isClientPortal);

  const client =
    data?.clients.find((item) => item.clientSlug === selectedClient) ??
    (session?.tenantSlug
      ? {
          clientSlug: session.tenantSlug,
          displayName: session.tenantSlug.toUpperCase(),
          spendMtd: 0,
          spendDailyMtd: 0,
          budgetMonthEst: 0,
          pacingPct: 0,
          status: "OK",
          alertCount: 0,
          criticalCount: 0,
        }
      : data?.clients[0]);
  if (!data || !client) return null;

  if (selectedClient === "ALL") {
    return (
      <div className="page">
        <section className="empty-selection">
          <span className="empty-selection-mark">W</span>
          <h1>Elige un cliente para abrir su sala de control</h1>
          <p>El selector del header persistirá cuando vuelvas a Morning Check o cambies de módulo.</p>
          <div className="client-picker-grid">
            {data.clients.map((item) => (
              <button
                type="button"
                key={item.clientSlug}
                onClick={() => setSelectedClient(item.clientSlug)}
              >
                <span>{item.displayName}</span>
                <small>{item.alertCount} alertas</small>
                <ArrowUpRight size={15} />
              </button>
            ))}
          </div>
        </section>
      </div>
    );
  }

  const alerts = data.alerts.filter((item) => item.clientSlug === client.clientSlug);
  const criticalAlerts = alerts.filter((item) => item.severity === "CRITICA").length;
  const health = data.health[client.clientSlug];
  const healthRank: Record<string, number> = { ROJO: 3, AMARILLO: 2, VERDE: 1, NO_APLICA: 0 };
  const seoHealth = health
    ? [health.gscStatus, health.ga4Status]
        .sort((left, right) => (healthRank[right] ?? 0) - (healthRank[left] ?? 0))[0]
    : null;
  const healthTone = (status: string | undefined) => status === "ROJO" ? "danger" : status === "AMARILLO" ? "warning" : "good";
  const freshness = (days: number | null | undefined) => days == null ? "s/d" : days === 0 ? "hoy" : `${days}d`;
  const pendingHealthValue = selectedClientHealthState === "error" ? "NO DISPONIBLE" : "CARGANDO";
  const pendingHealthNote = selectedClientHealthState === "error"
    ? "No fue posible consultar frescura"
    : "Consultando frescura";

  const accountSummary = <>
    <section className="account-summary-heading">
      <div><span className="eyebrow">Salud de cuenta</span><h2>Lo que requiere atención ahora</h2></div>
      <p>SEO y Paid se leen por separado; la frescura indica si los indicadores son confiables antes de actuar.</p>
    </section>
    <section className="metric-grid account-health-grid">
      <MetricCard
        label="Inversión MTD"
        value={currency.format(client.spendMtd)}
        note="corte atribución"
        icon={CircleDollarSign}
        delay={40}
      />
      <MetricCard
        label="Ritmo Paid"
        value={`${Math.round(client.pacingPct * 100)}%`}
        note={pacingLabel(client.status, client.pacingPct)}
        icon={Gauge}
        tone={client.status === "OVER" ? "danger" : client.status === "AT_RISK" ? "warning" : "good"}
        delay={80}
      />
      <MetricCard
        label="Datos SEO"
        value={seoHealth ?? pendingHealthValue}
        note={health ? `GSC ${freshness(health.gscDays)} · GA4 ${freshness(health.ga4Days)}` : pendingHealthNote}
        icon={ShieldCheck}
        tone={seoHealth ? healthTone(seoHealth) : "neutral"}
        delay={120}
      />
      <MetricCard
        label="Datos Paid"
        value={health?.paidStatus ?? pendingHealthValue}
        note={health ? `${health.paidPlatform ?? "Paid"} · ${freshness(health.paidDays)}` : pendingHealthNote}
        icon={ShieldCheck}
        tone={health ? healthTone(health.paidStatus) : "neutral"}
        delay={160}
      />
      <MetricCard
        label="Alertas"
        value={String(alerts.length)}
        note={`${criticalAlerts} críticas`}
        icon={Activity}
        tone={criticalAlerts ? "danger" : "good"}
        delay={200}
      />
    </section>
    <section className="client-alerts-section account-alerts">
      <section className="panel">
        <div className="panel-heading">
          <div>
            <span className="eyebrow orange">Prioridades</span>
            <h2>Alertas del cliente</h2>
          </div>
          <span className="count-badge">{alerts.length}</span>
        </div>
        <div className="stack-list">
          {alerts.map((alert, index) => (
            <AlertCard key={alert.id} alert={alert} index={index} />
          ))}
          {!alerts.length && <div className="empty-column"><p>Sin alertas activas.</p></div>}
        </div>
      </section>
    </section>
  </>;

  return (
    <div className="page">
      <ClientDetail clientSlug={client.clientSlug} summary={isClientScoped ? null : accountSummary} />
    </div>
  );
}

